import React, { useState, useEffect } from "react";
import {
  Paper,
  TextField,
  Button,
  Grid,
  FormControlLabel,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  CircularProgress,
  InputAdornment,
  IconButton
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Visibility, VisibilityOff, Send } from "@material-ui/icons";
import { toast } from "react-toastify";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(3),
    display: "flex",
    flexDirection: "column",
    width: "100%",
  },
  formField: {
    marginBottom: theme.spacing(2),
  },
  buttonContainer: {
    marginTop: theme.spacing(3),
    display: "flex",
    gap: theme.spacing(2),
  },
  testButton: {
    marginLeft: "auto",
  },
  infoText: {
    marginBottom: theme.spacing(3),
    color: theme.palette.text.secondary,
  },
}));

const EmailSettings = () => {
  const classes = useStyles();
  
  const [formData, setFormData] = useState({
    smtpHost: "",
    smtpPort: 587,
    smtpUser: "",
    smtpPassword: "",
    smtpFrom: "",
    smtpFromName: "Sistema CRM",
    smtpSecure: false,
    isActive: true,
  });

  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [sendingTest, setSendingTest] = useState(false);
  const [hasExistingSettings, setHasExistingSettings] = useState(false);

  useEffect(() => {
    loadEmailSettings();
  }, []);

  const loadEmailSettings = async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/email-settings");
      
      if (data) {
        setFormData({
          smtpHost: data.smtpHost || "",
          smtpPort: data.smtpPort || 587,
          smtpUser: data.smtpUser || "",
          smtpPassword: data.smtpPassword || "",
          smtpFrom: data.smtpFrom || "",
          smtpFromName: data.smtpFromName || "Sistema CRM",
          smtpSecure: data.smtpSecure || false,
          isActive: data.isActive !== undefined ? data.isActive : true,
        });
        setHasExistingSettings(true);
      }
    } catch (error) {
      console.log("Nenhuma configuração encontrada");
      setHasExistingSettings(false);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field) => (event) => {
    const value = event.target.type === "checkbox" 
      ? event.target.checked 
      : event.target.value;
    
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = async () => {
    try {
      setLoading(true);

      // Validações básicas
      if (!formData.smtpHost || !formData.smtpUser || !formData.smtpPassword || !formData.smtpFrom) {
        toast.error(i18n.t("settings.email.toasts.requiredFields"));
        return;
      }

      if (hasExistingSettings) {
        await api.put("/email-settings", formData);
        toast.success(i18n.t("settings.email.toasts.updated"));
      } else {
        await api.post("/email-settings", formData);
        toast.success(i18n.t("settings.email.toasts.saved"));
        setHasExistingSettings(true);
      }
    } catch (error) {
      console.error("Erro ao salvar configurações:", error);
      toast.error(
        error.response?.data?.message || i18n.t("settings.email.toasts.saveError")
      );
    } finally {
      setLoading(false);
    }
  };

  const handleOpenTestDialog = () => {
    if (!formData.smtpHost || !formData.smtpUser || !formData.smtpPassword || !formData.smtpFrom) {
      toast.warning(i18n.t("settings.email.toasts.fillBeforeTest"));
      return;
    }
    setTestDialogOpen(true);
  };

  const handleCloseTestDialog = () => {
    setTestDialogOpen(false);
    setTestEmail("");
  };

  const handleSendTestEmail = async () => {
    if (!testEmail || !testEmail.includes("@")) {
      toast.error(i18n.t("settings.email.toasts.invalidEmail"));
      return;
    }

    try {
      setSendingTest(true);
      const { data } = await api.post("/email-settings/test", {
        ...formData,
        testEmail,
      });

      if (data.success) {
        toast.success(data.message);
        handleCloseTestDialog();
      } else {
        toast.error(data.message || i18n.t("settings.email.toasts.testError"));
      }
    } catch (error) {
      console.error("Erro ao enviar email de teste:", error);
      toast.error(
        error.response?.data?.message || i18n.t("settings.email.toasts.testError")
      );
    } finally {
      setSendingTest(false);
    }
  };

  if (loading && !hasExistingSettings) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: "40px" }}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <Paper className={classes.paper}>
      <Typography variant="h6" gutterBottom>
        {i18n.t("settings.email.title")}
      </Typography>
      <Typography className={classes.infoText} variant="body2">
        {i18n.t("settings.email.description")}
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <TextField
            fullWidth
            label={`${i18n.t("settings.email.fields.host")} *`}
            value={formData.smtpHost}
            onChange={handleChange("smtpHost")}
            placeholder={i18n.t("settings.email.fields.hostPlaceholder")}
            variant="outlined"
            className={classes.formField}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label={`${i18n.t("settings.email.fields.port")} *`}
            type="number"
            value={formData.smtpPort}
            onChange={handleChange("smtpPort")}
            variant="outlined"
            className={classes.formField}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label={`${i18n.t("settings.email.fields.user")} *`}
            value={formData.smtpUser}
            onChange={handleChange("smtpUser")}
            placeholder={i18n.t("settings.email.fields.userPlaceholder")}
            variant="outlined"
            className={classes.formField}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label={`${i18n.t("settings.email.fields.password")} *`}
            type={showPassword ? "text" : "password"}
            value={formData.smtpPassword}
            onChange={handleChange("smtpPassword")}
            variant="outlined"
            className={classes.formField}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label={`${i18n.t("settings.email.fields.from")} *`}
            value={formData.smtpFrom}
            onChange={handleChange("smtpFrom")}
            placeholder={i18n.t("settings.email.fields.fromPlaceholder")}
            variant="outlined"
            className={classes.formField}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label={i18n.t("settings.email.fields.fromName")}
            value={formData.smtpFromName}
            onChange={handleChange("smtpFromName")}
            placeholder={i18n.t("settings.email.fields.fromNamePlaceholder")}
            variant="outlined"
            className={classes.formField}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControlLabel
            control={
              <Switch
                checked={formData.smtpSecure}
                onChange={handleChange("smtpSecure")}
                color="primary"
              />
            }
            label={i18n.t("settings.email.fields.secure")}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControlLabel
            control={
              <Switch
                checked={formData.isActive}
                onChange={handleChange("isActive")}
                color="primary"
              />
            }
            label={i18n.t("settings.email.fields.active")}
          />
        </Grid>
      </Grid>

      <div className={classes.buttonContainer}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={loading}
        >
          {loading ? <CircularProgress size={24} /> : hasExistingSettings ? i18n.t("settings.email.buttons.update") : i18n.t("common.save")}
        </Button>

        <Button
          variant="outlined"
          color="primary"
          onClick={handleOpenTestDialog}
          startIcon={<Send />}
          className={classes.testButton}
        >
          {i18n.t("settings.email.buttons.test")}
        </Button>
      </div>

      {/* Dialog para testar email */}
      <Dialog open={testDialogOpen} onClose={handleCloseTestDialog}>
        <DialogTitle>{i18n.t("settings.email.testDialog.title")}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" style={{ marginBottom: 16 }}>
            {i18n.t("settings.email.testDialog.description")}
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label={i18n.t("settings.email.testDialog.email")}
            type="email"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            placeholder={i18n.t("settings.email.testDialog.emailPlaceholder")}
            variant="outlined"
            onKeyPress={(e) => {
              if (e.key === "Enter") {
                handleSendTestEmail();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseTestDialog} color="default">
            {i18n.t("common.cancel")}
          </Button>
          <Button
            onClick={handleSendTestEmail}
            color="primary"
            variant="contained"
            disabled={sendingTest}
          >
            {sendingTest ? <CircularProgress size={24} /> : i18n.t("settings.email.testDialog.send")}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default EmailSettings;
